import { useEffect } from 'react';

/**
 * Dynamic SEO Component for Transcontinental Network
 * Updates document head title, meta descriptions, keywords, Open Graph tags, canonical links,
 * and injects Schema.org JSON-LD structured data for Camera Installation & IT Services.
 */
const SEO = ({
  title = 'CCTV & Security Camera Installation Services | Transcontinental Network',
  description = 'Expert CCTV & Security Camera Installation services, 4K IP camera systems, NVR surveillance setups, enterprise IT networking, and web development solutions by Transcontinental Network.',
  keywords = 'security camera installation, CCTV installation services, IP camera setup, commercial surveillance installation, 4K security cameras, NVR installation, IT networking, Transcontinental Network',
  canonical = 'https://transcontinentalnetwork.com/',
  ogImage = '/logo512.png',
  schema = null
}) => {
  useEffect(() => {
    // 1. Update Document Title
    document.title = title;

    // Helper function to update or create meta tags
    const updateMetaTag = (selector, attribute, attributeValue, contentValue) => {
      let element = document.querySelector(`meta[${selector}="${attributeValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(selector, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', contentValue);
    };

    // 2. Update Primary Meta Tags
    updateMetaTag('name', 'description', 'description', description);
    updateMetaTag('name', 'keywords', 'keywords', keywords);
    updateMetaTag('name', 'title', 'title', title);

    // 3. Update Open Graph Tags
    updateMetaTag('property', 'og:title', 'og:title', title);
    updateMetaTag('property', 'og:description', 'og:description', description);
    updateMetaTag('property', 'og:image', 'og:image', ogImage);
    updateMetaTag('property', 'og:url', 'og:url', canonical);

    // 4. Update Twitter Card Tags
    updateMetaTag('property', 'twitter:title', 'twitter:title', title);
    updateMetaTag('property', 'twitter:description', 'twitter:description', description);
    updateMetaTag('property', 'twitter:image', 'twitter:image', ogImage);
    updateMetaTag('property', 'twitter:url', 'twitter:url', canonical);

    // 5. Update Canonical Link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);

    // 6. Inject Schema.org JSON-LD Structured Data
    const scriptId = 'json-ld-seo-schema';
    let existingScript = document.getElementById(scriptId);

    const defaultSchema = schema || {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LocalBusiness',
          '@id': 'https://transcontinentalnetwork.com/#organization',
          'name': 'Transcontinental Network',
          'url': 'https://transcontinentalnetwork.com',
          'logo': 'https://transcontinentalnetwork.com/logo512.png',
          'image': 'https://transcontinentalnetwork.com/logo512.png',
          'description': 'Professional CCTV and Security Camera Installation Services, IP Camera Systems, NVR setups, IT networking, and web development.',
          'priceRange': '$$',
          'areaServed': 'Global',
          'hasOfferCatalog': {
            '@type': 'OfferCatalog',
            'name': 'Camera Installation & IT Services',
            'itemListElement': [
              {
                '@type': 'Offer',
                'itemOffered': {
                  '@type': 'Service',
                  'name': 'CCTV & IP Camera Installation',
                  'description': 'Professional installation of 4K IP security cameras, night vision cameras, and motion detection surveillance systems.'
                }
              },
              {
                '@type': 'Offer',
                'itemOffered': {
                  '@type': 'Service',
                  'name': 'NVR & Surveillance System Configuration',
                  'description': 'Network Video Recorder setup, remote monitoring access, and multi-channel camera storage integration.'
                }
              },
              {
                '@type': 'Offer',
                'itemOffered': {
                  '@type': 'Service',
                  'name': 'Enterprise IT Networking & Web Development',
                  'description': 'End-to-end network cabling, router/switch deployment, web development, and cloud hosting services.'
                }
              }
            ]
          }
        }
      ]
    };

    if (existingScript) {
      existingScript.textContent = JSON.stringify(defaultSchema);
    } else {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(defaultSchema);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonical, ogImage, schema]);

  return null;
};

export default SEO;
